"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function AapkaVisionHamariDesign() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223822" id="ready-for-a-fresh-start" className="dark bg-primary text-white py-32 md:py-48 px-6 relative overflow-hidden flex items-center justify-center min-h-[80vh]">
      {}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,var(--color-secondary)_0%,transparent_100%)] opacity-10" />

      <div ref={ref} className="max-w-4xl mx-auto text-center flex flex-col items-center justify-center relative z-10">
        <h2 className={`font-default text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-tight mb-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          Ready for a
          <br />
          <span className="text-white">
            Fresh Start?
          </span>
        </h2>
        
        <p className={`font-default text-4xl md:text-5xl text-secondary-foreground font-bold max-w-2xl mb-12 transition-all duration-1000 ease-out delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          (772) 555-0199
        </p>
        
        <div className={`transition-all duration-1000 ease-out delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" className="rounded-full px-8 py-7 text-lg group bg-accent text-accent-foreground hover:bg-accent/90 border border-transparent transition-all duration-300 shadow-lg">
            <Link to="#">
              Book Online
              <ArrowRight className="ml-3 size-5 transition-transform duration-300 group-hover:translate-x-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}